import { useQuery } from '@apollo/client';
import React, { SyntheticEvent, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Box, Stack } from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_SITES_QUERY,
} from '../../../../gql';
import { FilterInput } from '../../../FilterInput';
import { FindFilterValue } from '../types';
import { BooleanFilterToggle } from './BooleanFilterToggle';
import { useLocale, useVocabulary } from '../../../../i18n';
import { Button, MaterialIndicator } from '../../../design-system';
import { NO_SITE_FILTER_VALUE } from '../../../../consts';

const filterMessages = defineMessages({
  era: { id: 'filter.era', defaultMessage: 'Filter by era' },
  findType: {
    id: 'filter.findType',
    defaultMessage: 'Filter by find type',
  },
  group: { id: 'filter.group', defaultMessage: 'Filter by group' },
  material: {
    id: 'filter.material',
    defaultMessage: 'Filter by material',
  },
  site: { id: 'filter.site', defaultMessage: 'Filter by site' },
  tag: { id: 'filter.tag', defaultMessage: 'Filter by tag' },
  images: { id: 'filter.images', defaultMessage: 'Filter by images' },
  allImages: { id: 'filter.allImages', defaultMessage: 'All' },
  withImages: { id: 'filter.withImages', defaultMessage: 'With images' },
  withoutImages: {
    id: 'filter.withoutImages',
    defaultMessage: 'Without images',
  },
  amcrId: { id: 'filter.amcrId', defaultMessage: 'Filter by PAS ID' },
  allAmcrIds: { id: 'filter.allAmcrIds', defaultMessage: 'All' },
  withAmcrId: { id: 'filter.withAmcrId', defaultMessage: 'With PAS ID' },
  withoutAmcrId: {
    id: 'filter.withoutAmcrId',
    defaultMessage: 'Without PAS ID',
  },
});

const builderStyles = { margin: '20px 0' };
const compactBuilderStyles = { mt: 1, mb: 0 };
const renderMaterialIndicator = (option: { id: string }) => (
  <MaterialIndicator material={option.id} />
);

interface SiteFilterOption {
  id: string;
  name: string;
}

interface FilterBuilderProps {
  filter: FindFilterValue | null;
  onChange: (filter: FindFilterValue) => void;
  onRemove: () => void;
  showEraFilter?: boolean;
  showFindTypeFilter?: boolean;
  showGroupFilter?: boolean;
  showImageFilter?: boolean;
  showAmcrIdFilter?: boolean;
  showMaterialFilter?: boolean;
  showSiteFilter?: boolean;
  showTagFilter?: boolean;
  showRemoveButton?: boolean;
  compact?: boolean;
  neutralStyle?: boolean;
}

const FilterBuilder = ({
  filter,
  onChange,
  onRemove,
  showEraFilter = false,
  showFindTypeFilter = false,
  showGroupFilter = false,
  showImageFilter = false,
  showAmcrIdFilter = false,
  showMaterialFilter = false,
  showSiteFilter = false,
  showTagFilter = false,
  showRemoveButton = true,
  compact = false,
  neutralStyle = true,
}: FilterBuilderProps) => {
  const intl = useIntl();
  const { locale } = useLocale();
  const getVocabularyLabel = useVocabulary();
  const collator = useMemo(() => new Intl.Collator(locale), [locale]);
  const {
    data: availableFiltersData,
    loading: availableFiltersLoading,
  } = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const { data: sitesData, loading: sitesLoading } = useQuery(
    GET_MY_SITES_QUERY,
    {
      fetchPolicy: 'cache-first',
      skip: !showSiteFilter,
    }
  );

  const eraOptions = useMemo(() => {
    const rawOptions = (
      availableFiltersData?.getAllFilterTypes?.eras || []
    ).map((era) => ({
      ...era,
      id: era.id.toLocaleLowerCase(),
      parentId: era.parentId?.toLocaleLowerCase() ?? null,
      label: getVocabularyLabel('era', era.id),
    }));

    return rawOptions
      .filter((i) => !i.parentId)
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((i) => [i, ...rawOptions.filter((o) => o.parentId === i.id)])
      .flat();
  }, [availableFiltersData?.getAllFilterTypes?.eras, getVocabularyLabel]);

  const findTypeOptions = useMemo(() => {
    const rawOptions = availableFiltersData?.getAllFilterTypes?.findTypes || [];
    const orderedOptions = rawOptions
      .reduce(
        (acc, type) =>
          acc.includes(type.category) ? acc : [...acc, type.category],
        []
      )
      .sort((a, b) => collator.compare(a, b))
      .map((i) => [
        {
          id: i,
          category: null,
          label: getVocabularyLabel('findType', i),
        },
        ...rawOptions
          .filter((o) => o.category === i)
          .map((option) => ({
            ...option,
            label: getVocabularyLabel('findType', option.id),
          }))
          .sort((a, b) => collator.compare(a.label, b.label)),
      ])
      .flat();

    return orderedOptions;
  }, [
    availableFiltersData?.getAllFilterTypes?.findTypes,
    collator,
    getVocabularyLabel,
  ]);

  const materialsOptions = useMemo(
    () =>
      (availableFiltersData?.getAllFilterTypes?.materials || [])
        .map((material) => ({
          id: material,
          label: getVocabularyLabel('material', material),
        }))
        .toSorted((a, b) => collator.compare(a.label, b.label)),
    [
      availableFiltersData?.getAllFilterTypes?.materials,
      collator,
      getVocabularyLabel,
    ]
  );

  const tagsOptions = useMemo(
    () =>
      (availableFiltersData?.getAllFilterTypes?.tags || [])
        .toSorted((a, b) => a.name - b.name)
        .map((m) => ({ id: m.name })),
    [availableFiltersData?.getAllFilterTypes?.tags]
  );

  const groupsOptions = useMemo(
    () =>
      (availableFiltersData?.getAllFilterTypes?.groups || []).toSorted(
        (a, b) => a.name - b.name
      ),
    [availableFiltersData?.getAllFilterTypes?.groups]
  );

  const siteOptions = useMemo<SiteFilterOption[]>(
    () => [
      {
        id: NO_SITE_FILTER_VALUE,
        name: intl.formatMessage({
          id: 'general.none',
          defaultMessage: 'None',
        }),
      },
      ...(sitesData?.getMySites || []),
    ],
    [intl, sitesData?.getMySites]
  );

  const currentFilter = filter || {};

  const selectedEraIds = currentFilter.eras?.map((era) =>
    era.toLocaleLowerCase()
  );
  const defaultEras = selectedEraIds
    ? eraOptions.filter((o) => selectedEraIds.includes(o.id))
    : [];

  const defaultSites = currentFilter.sites
    ? siteOptions.filter((option) => currentFilter.sites.includes(option.id))
    : [];

  const defaultFindTypes = currentFilter.findTypes
    ? findTypeOptions.filter((o) => currentFilter.findTypes.includes(o.id))
    : [];

  const defaultMaterials = currentFilter.materials
    ? materialsOptions.filter((o) => currentFilter.materials.includes(o.id))
    : [];

  const defaultTags = currentFilter.tags
    ? tagsOptions.filter((o) => currentFilter.tags.includes(o.id))
    : [];

  const defaultGroups = currentFilter.groups
    ? groupsOptions.filter((o) => currentFilter.groups.includes(o.id))
    : [];

  const visibleFilterTypeCount = [
    showEraFilter,
    showSiteFilter,
    showFindTypeFilter,
    showMaterialFilter,
    showGroupFilter,
    showTagFilter,
    showImageFilter,
    showAmcrIdFilter,
  ].filter(Boolean).length;

  const handleErasChange = (e: SyntheticEvent<Element, Event>, value: any) => {
    const updatedEras = value.map((item) => item.id);

    if (updatedEras.length) {
      onChange({ ...currentFilter, eras: updatedEras });
    } else {
      const { eras, ...rest } = currentFilter;
      onChange(rest);
    }
  };

  const handleSitesChange = (e: SyntheticEvent<Element, Event>, value: any) => {
    const updatedSites = value.map((item) => item.id);

    if (updatedSites.length) {
      onChange({ ...currentFilter, sites: updatedSites });
    } else {
      const { sites, ...rest } = currentFilter;
      onChange(rest);
    }
  };

  const handleFindTypesChange = (
    e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    const updatedFindTypes = value.map((item) => item.id);

    if (updatedFindTypes.length) {
      onChange({ ...currentFilter, findTypes: updatedFindTypes });
    } else {
      const { findTypes, ...rest } = currentFilter;
      onChange(rest);
    }
  };

  const handleMaterialsChange = (
    e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    const updatedMaterials = value.map((item) => item.id);

    if (updatedMaterials.length) {
      onChange({ ...currentFilter, materials: updatedMaterials });
    } else {
      const { materials, ...rest } = currentFilter;
      onChange(rest);
    }
  };

  const handleTagsChange = (e: SyntheticEvent<Element, Event>, value: any) => {
    const updatedTags = value.map((item) => item.id);

    if (updatedTags.length) {
      onChange({ ...currentFilter, tags: updatedTags });
    } else {
      const { tags, ...rest } = currentFilter;
      onChange(rest);
    }
  };

  const handleGroupsChange = (
    e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    const updatedGroups = value.map((item) => item.id);

    if (updatedGroups.length) {
      onChange({ ...currentFilter, groups: updatedGroups });
    } else {
      const { groups, ...rest } = currentFilter;
      onChange(rest);
    }
  };

  const handleImagesChange = (value: boolean | undefined) => {
    if (value == null) {
      const { hasImages, ...rest } = currentFilter;
      onChange(rest);
    } else {
      onChange({ ...currentFilter, hasImages: value });
    }
  };

  const handleAmcrIdChange = (value: boolean | undefined) => {
    if (value == null) {
      const { hasAmcrId, ...rest } = currentFilter;
      onChange(rest);
    } else {
      onChange({ ...currentFilter, hasAmcrId: value });
    }
  };

  if (availableFiltersLoading || (showSiteFilter && sitesLoading)) {
    return (
      <div>
        <FormattedMessage id="loading" defaultMessage="Loading…" />
      </div>
    );
  }

  return (
    <Stack sx={compact ? compactBuilderStyles : builderStyles} gap={1}>
      {showEraFilter && (
        <FilterInput
          onChange={handleErasChange}
          parent="parentId"
          label={intl.formatMessage(filterMessages.era)}
          optionLabelProperty="label"
          options={eraOptions}
          val={defaultEras}
          key="eras"
          neutral={neutralStyle}
        />
      )}
      {showSiteFilter && (
        <FilterInput
          onChange={handleSitesChange}
          label={intl.formatMessage(filterMessages.site)}
          optionLabelProperty="name"
          options={siteOptions}
          key="sites"
          val={defaultSites}
          neutral={neutralStyle}
        />
      )}
      {showFindTypeFilter && (
        <FilterInput
          onChange={handleFindTypesChange}
          parent="category"
          label={intl.formatMessage(filterMessages.findType)}
          optionLabelProperty="label"
          options={findTypeOptions}
          key="find-types"
          val={defaultFindTypes}
          neutral={neutralStyle}
        />
      )}
      {showMaterialFilter && (
        <FilterInput
          onChange={handleMaterialsChange}
          label={intl.formatMessage(filterMessages.material)}
          optionLabelProperty="label"
          options={materialsOptions}
          renderIndicator={renderMaterialIndicator}
          key="materials"
          val={defaultMaterials}
          neutral={neutralStyle}
        />
      )}
      {showGroupFilter && (
        <FilterInput
          onChange={handleGroupsChange}
          label={intl.formatMessage(filterMessages.group)}
          options={groupsOptions}
          optionLabelProperty="name"
          key="groups"
          val={defaultGroups}
          neutral={neutralStyle}
        />
      )}
      {showTagFilter && (
        <FilterInput
          onChange={handleTagsChange}
          label={intl.formatMessage(filterMessages.tag)}
          options={tagsOptions}
          key="tags"
          val={defaultTags}
          neutral={neutralStyle}
        />
      )}
      {showImageFilter && (
        <BooleanFilterToggle
          key="images"
          label={intl.formatMessage(filterMessages.images)}
          allLabel={intl.formatMessage(filterMessages.allImages)}
          withLabel={intl.formatMessage(filterMessages.withImages)}
          withoutLabel={intl.formatMessage(filterMessages.withoutImages)}
          value={currentFilter.hasImages}
          onChange={handleImagesChange}
        />
      )}
      {showAmcrIdFilter && (
        <BooleanFilterToggle
          key="amcr-id"
          label={intl.formatMessage(filterMessages.amcrId)}
          allLabel={intl.formatMessage(filterMessages.allAmcrIds)}
          withLabel={intl.formatMessage(filterMessages.withAmcrId)}
          withoutLabel={intl.formatMessage(filterMessages.withoutAmcrId)}
          value={currentFilter.hasAmcrId}
          onChange={handleAmcrIdChange}
        />
      )}
      {showRemoveButton && visibleFilterTypeCount > 0 && (
        <Box py={2}>
          <Button
            startIcon={<FilterAltOffIcon />}
            tone="danger"
            onClick={onRemove}
          >
            {visibleFilterTypeCount > 1 ? (
              <FormattedMessage
                id="filter.removeMultiple"
                defaultMessage="Remove filters"
              />
            ) : (
              <FormattedMessage
                id="filter.remove"
                defaultMessage="Remove filter"
              />
            )}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export { FilterBuilder };
